import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
  Typography, Box, Divider, Autocomplete, useMediaQuery, useTheme,
  FormControlLabel, Checkbox, Select, MenuItem, FormControl, InputLabel,
  Chip, Stack, IconButton, Tooltip, CircularProgress, InputAdornment, Alert, Link,
  Accordion, AccordionSummary, AccordionDetails,
  ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HistoryIcon from '@mui/icons-material/History';
import es from 'date-fns/locale/es';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import {
  createAppointment, updateAppointment, deleteAppointment, createPatient,
  checkInAppointment, getBillingList, createPayment, updatePayment, deletePayment,
  getAppointmentTypes, createAppointmentType, getPatients, getDoctorAvailableSlots, getWaitlist, getAppointmentHistory,
  logPatientMessage, sendAppointmentEmail, searchPatientsByDni,
} from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useNotification } from '../hooks/useNotification';
import { useClinicFeatures } from '../hooks/useClinicFeatures';
import { APPOINTMENT_STATE_CONFIG as STATE_CONFIG, APPOINTMENT_STATE_OPTIONS as STATE_OPTIONS } from '../constants/appointmentStates';
import { openWhatsApp } from './patient-profile/patientProfileHelpers';
import SelfUpdateLinkDialog from './SelfUpdateLinkDialog';

const PAYMENT_STATUS_CONFIG = {
  pendiente:   { label: 'Pendiente',   color: 'warning' },
  'seña':      { label: 'Seña',        color: 'secondary' },
  pagado:      { label: 'Pagado',      color: 'success' },
  obra_social: { label: 'Obra social', color: 'info' },
  exento:      { label: 'Exento',      color: 'default' },
};

const PAYMENT_METHODS = [
  { value: 'efectivo',      label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'tarjeta',       label: 'Tarjeta' },
  { value: 'obra_social',   label: 'Obra social' },
];

const EMPTY_PAYMENT = { amount: '', status: 'pendiente', payment_method: '', notes: '' };


const buildWhatsAppMessage = (body, { patientName, fecha, hora, doctorName }) =>
  body
    .replace(/{patient_name}/g, patientName)
    .replace(/{fecha}/g, fecha)
    .replace(/{hora}/g, hora)
    .replace(/{doctor_name}/g, doctorName);

const HISTORY_FIELD_LABELS = {
  state: 'Estado',
  start_time: 'Fecha y hora',
  duration_minutes: 'Duración',
  notes: 'Notas',
  cancellation_reason: 'Motivo de cancelación',
  appointment_type_id: 'Tipo de turno',
  is_overbooked: 'Sobreturno',
};

const formatHistoryValue = (field, value) => {
  if (value === null || value === undefined) return '—';
  if (field === 'state') return STATE_CONFIG[value]?.label ?? value;
  if (field === 'start_time') {
    try { return new Date(value.endsWith('Z') ? value : value + 'Z').toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' }); }
    catch { return value; }
  }
  if (field === 'duration_minutes') return `${value} min`;
  if (field === 'is_overbooked') return value ? 'Sí' : 'No';
  return String(value);
};

const AppointmentHistoryEntry = ({ entry }) => {
  let detalle = {};
  try { detalle = entry.detalle ? JSON.parse(entry.detalle) : {}; } catch { /* legacy plain string */ }
  const changes = detalle?.changes || {};
  const fecha = new Date(entry.fecha.endsWith('Z') ? entry.fecha : entry.fecha + 'Z');

  return (
    <Box sx={{ borderLeft: '2px solid', borderColor: 'divider', pl: 1.25 }}>
      <Typography variant="caption" color="text.secondary">
        {fecha.toLocaleString('es-AR', { dateStyle: 'short', timeStyle: 'short' })} — {entry.username} ({entry.user_role})
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {entry.accion === 'CREAR' ? 'Turno creado'
          : entry.accion === 'ELIMINAR' ? 'Turno eliminado'
          : entry.accion === 'CONFIRMAR' ? 'Confirmado por el paciente'
          : entry.accion === 'CANCELAR' ? 'Cancelado por el paciente'
          : 'Turno editado'}
      </Typography>
      {Object.keys(changes).length > 0 && (
        <Stack spacing={0.25} sx={{ mt: 0.25 }}>
          {Object.entries(changes).map(([field, { from, to }]) => (
            <Typography key={field} variant="caption" color="text.secondary">
              {HISTORY_FIELD_LABELS[field] || field}: {formatHistoryValue(field, from)} → {formatHistoryValue(field, to)}
            </Typography>
          ))}
        </Stack>
      )}
    </Box>
  );
};

// Bloque colapsable reutilizado por Pagos / WhatsApp / Emails / Historial —
// mismo look en los 4 para que el popup no se sienta como piezas sueltas.
const SectionAccordion = React.forwardRef(({ icon, title, badge, expanded, onChange, children }, ref) => (
  <Accordion
    ref={ref}
    expanded={expanded}
    onChange={onChange}
    disableGutters
    elevation={0}
    sx={{
      border: '1px solid', borderColor: 'divider', borderRadius: 1.5, overflow: 'hidden',
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1, my: 0.75 } }}>
      {icon}
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{title}</Typography>
      {badge}
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>
      {children}
    </AccordionDetails>
  </Accordion>
));

const AppointmentDialog = ({
  open, onClose, isEdit, selectedEvent,
  doctorId, doctor, patients, setPatients, waTemplates,
  onSaved,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const notify = useNotification();
  const { isAdmin, isRecepcionista, isMedico } = useAuth();
  const { features } = useClinicFeatures();
  // Médico: control total sobre su propia agenda (crear, reprogramar,
  // cambiar paciente/tipo/duración/estado, eliminar) — el backend ya
  // valida que solo pueda tocar turnos con doctor_id == su propio médico
  // vinculado, así que acá alcanza con isMedico: el calendario del médico
  // solo muestra su propia agenda, nunca la de otro.
  const canEdit = isAdmin || isRecepcionista || isMedico;
  const [checkingIn, setCheckingIn] = useState(false);

  // Billing: el médico también puede registrar el cobro de sus propias
  // consultas (pedido explícito: "todo lo que hace recepción, si es su
  // agenda/paciente") — el backend valida que el pago quede ligado a un
  // turno de su propia agenda.
  const billingEnabled = features?.billing && isEdit && canEdit;
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentForm, setPaymentForm] = useState(null); // null=closed, {}=new, {id,...}=edit
  const [paymentSaving, setPaymentSaving] = useState(false);
  // Antes el panel de Pagos exigía isEdit (turno ya guardado, con id real
  // al que asociar el pago) — pedido explícito: poder cargar la seña al
  // crear el turno, sin tener que guardar primero y volver a entrar. Al
  // crear, no hay historial de pagos que listar/editar (el turno recién
  // existe), así que es un mini-form de un solo pago opcional, no el panel
  // completo de edición — ver newTurnoPayment / handleSubmit.
  const billingEnabledForNew = features?.billing && !isEdit && canEdit;
  const [newTurnoPayment, setNewTurnoPayment] = useState(null); // null=colapsado, {}=cargando datos
  const selfUpdateEnabled = features?.patient_self_update;

  const fetchPayments = useCallback(async () => {
    if (!billingEnabled || !selectedEvent?.id) return;
    setPaymentsLoading(true);
    try {
      const r = await getBillingList({ appointment_id: selectedEvent.id, limit: 20 });
      setPayments(r.data?.items || r.data || []);
    } catch { /* silent */ }
    finally { setPaymentsLoading(false); }
  }, [billingEnabled, selectedEvent?.id]);
  const [appointmentData, setAppointmentData] = useState({
    patient_id: '', appointment_type_id: '', duration_minutes: 30, notes: '', state: 'pendiente', is_overbooked: false, cancellation_reason: '',
  });
  const [startDateTime, setStartDateTime] = useState(null);
  // "Fecha y hora" era un único DateTimePicker con máscara segmentada —
  // pedido explícito del recepcionista: tipear la hora directo (ej.
  // "1430") sin tener que navegar segmento por segmento con el teclado.
  // Se separa en un DatePicker (elegir el día con el calendario, donde el
  // picker visual sí tiene sentido) + un <input type="time"> nativo (donde
  // tipear es más rápido) — mismo patrón ya usado en Doctors.js para el
  // horario semanal. Ambos siguen escribiendo al mismo startDateTime
  // (un solo Date), así que el resto del componente no cambia.
  const setDatePart = (newDate) => {
    if (!newDate) return;
    setStartDateTime(prev => {
      const base = prev ? new Date(prev) : new Date();
      base.setFullYear(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
      return base;
    });
  };
  const setTimePart = (hhmm) => {
    if (!hhmm) return;
    const [h, m] = hhmm.split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return;
    setStartDateTime(prev => {
      const base = prev ? new Date(prev) : new Date();
      base.setHours(h, m, 0, 0);
      return base;
    });
  };
  const timeInputValue = startDateTime
    ? `${String(new Date(startDateTime).getHours()).padStart(2, '0')}:${String(new Date(startDateTime).getMinutes()).padStart(2, '0')}`
    : '';
  const [showQuickPatientForm, setShowQuickPatientForm] = useState(false);
  // +54 11 precargado por default (prefijo más común en la base de
  // pacientes) pero editable — pedido explícito del usuario. Se guarda
  // como texto libre igual que el resto del teléfono, el backend no
  // valida formato.
  const [quickPatient, setQuickPatient] = useState({ first_name: '', last_name: '', phone: '+54 11 ', email: '', dni: '' });
  const [quickPatientDniWarning, setQuickPatientDniWarning] = useState(null); // { id, name } del existente
  const [quickPatientDniChecking, setQuickPatientDniChecking] = useState(false);
  // Se ofrece apenas se crea paciente+turno en un solo submit — mismo
  // patrón/diálogo que "Generar link de actualización" en Patients.js.
  const [selfUpdateLinkFor, setSelfUpdateLinkFor] = useState(null); // { patientId, patientPhone } | null

  const checkQuickPatientDni = async (dni) => {
    if (!dni || !dni.trim()) { setQuickPatientDniWarning(null); return; }
    setQuickPatientDniChecking(true);
    try {
      const res = await searchPatientsByDni(dni.trim());
      const found = (res.data?.items || []).find(p => p.dni === dni.trim() && p.is_active);
      setQuickPatientDniWarning(found ? { id: found.id, name: found.name } : null);
    } catch { setQuickPatientDniWarning(null); }
    finally { setQuickPatientDniChecking(false); }
  };
  const [appointmentTypes, setAppointmentTypes] = useState([]);
  // Antes el selector de "Tipo de turno" ni aparecía si la clínica no
  // tenía ningún tipo cargado, y crear uno era exclusivo de Configuración
  // (solo admin) — pedido explícito: poder crear un tipo al vuelo desde
  // acá mismo si el que se necesita no está en la lista (ahora cualquier
  // rol que pueda crear turnos puede crear un tipo, ver appointment_types.py).
  const [showQuickTypeForm, setShowQuickTypeForm] = useState(false);
  const [quickType, setQuickType] = useState({ name: '', default_duration_minutes: 30, color: '#1565c0' });
  const [creatingType, setCreatingType] = useState(false);
  const [scheduleConflict, setScheduleConflict] = useState(null); // mensaje del backend, o null


  // Lista de espera del médico — se muestra al cancelar un turno con
  // paciente, para que recepción vea a quién ofrecerle el hueco liberado.
  // Sin notificación automática: es información para contactar manualmente.
  const [waitlistForDoctor, setWaitlistForDoctor] = useState([]);
  const isCancelingWithPatient = isEdit && appointmentData.state === 'cancelado'
    && selectedEvent?.state !== 'cancelado' && selectedEvent?.patient_id;

  useEffect(() => {
    if (!isCancelingWithPatient || !doctorId || !features?.waitlist) { setWaitlistForDoctor([]); return; }
    getWaitlist({ doctor_id: doctorId, status: 'activo' })
      .then(r => setWaitlistForDoctor(r.data || []))
      .catch(() => setWaitlistForDoctor([]));
  }, [isCancelingWithPatient, doctorId, features?.waitlist]);

  // Historial de cambios del turno — quién hizo qué y cuándo.
  const [historyEntries, setHistoryEntries] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  // Secciones colapsables (Pagos / Comunicación / Historial) — una sola
  // expandida a la vez para que el diálogo no crezca sin control. Al
  // expandir, se scrollea automático hacia esa sección — pedido explícito
  // del usuario: con poca destreza tecnológica, el usuario no se daba
  // cuenta de que la sección se había abierto más abajo en el diálogo.
  const [expandedSection, setExpandedSection] = useState(null);
  const sectionRefs = useRef({});
  const toggleSection = (name) => (_, isExpanded) => {
    setExpandedSection(isExpanded ? name : null);
    if (isExpanded) {
      setTimeout(() => {
        sectionRefs.current[name]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  };
  const showHistory = expandedSection === 'history';

  // Canal elegido para enviar la comunicación del turno — WhatsApp y Email
  // convivían antes como dos secciones separadas del acordeón; pedido
  // explícito del usuario: poder elegir uno u otro desde un solo lugar. Se
  // arranca en 'whatsapp' porque ya era el único canal con envío manual
  // (Email era 100% automático, sin botón).
  const [commChannel, setCommChannel] = useState('whatsapp');
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    if (!showHistory || !isEdit || !selectedEvent?.id || historyLoaded) return;
    setHistoryLoading(true);
    getAppointmentHistory(selectedEvent.id)
      .then(r => setHistoryEntries(r.data || []))
      .catch(() => setHistoryEntries([]))
      .finally(() => { setHistoryLoading(false); setHistoryLoaded(true); });
  }, [showHistory, isEdit, selectedEvent?.id, historyLoaded]);

  // Huecos libres del médico ese día — evita el ida-y-vuelta de "probar una
  // hora, ver el 409, probar otra" al mover/crear un turno.
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [showSlots, setShowSlots] = useState(false);

  const slotsDateKey = startDateTime ? new Date(startDateTime).toDateString() : null;

  useEffect(() => {
    if (!showSlots || !doctorId || !slotsDateKey) { setAvailableSlots([]); return; }
    let active = true;
    setSlotsLoading(true);
    const d = new Date(startDateTime);
    const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    getDoctorAvailableSlots(doctorId, {
      date: dateStr,
      duration_minutes: appointmentData.duration_minutes || 30,
      ...(isEdit && selectedEvent?.id ? { exclude_appointment_id: selectedEvent.id } : {}),
    })
      .then(r => { if (active) setAvailableSlots(r.data?.slots || []); })
      .catch(() => { if (active) setAvailableSlots([]); })
      .finally(() => { if (active) setSlotsLoading(false); });
    return () => { active = false; };
    // eslint-disable-next-line
  }, [showSlots, doctorId, slotsDateKey, appointmentData.duration_minutes]);

  // Búsqueda de paciente server-side (nombre/DNI/tel./email) con debounce —
  // reemplaza al filtro sobre el listado de 500 precargados, que se queda
  // corto en clínicas con más pacientes y no busca por DNI de forma confiable.
  const [patientSearchInput, setPatientSearchInput] = useState('');
  const [patientSearchResults, setPatientSearchResults] = useState([]);
  const [patientSearchLoading, setPatientSearchLoading] = useState(false);

  useEffect(() => {
    // Defensa adicional al reset de arriba: si el diálogo se cierra
    // MIENTRAS una búsqueda está en curso (debounce pendiente o request ya
    // en vuelo), este efecto también se re-ejecuta por el cambio de `open`
    // en sus deps — su cleanup corta el timer/ignora la respuesta tardía,
    // en vez de depender únicamente del reset al reabrir.
    if (!open || patientSearchInput.trim().length < 2) {
      setPatientSearchResults([]);
      setPatientSearchLoading(false);
      return;
    }
    let active = true;
    setPatientSearchLoading(true);
    const timer = setTimeout(() => {
      getPatients({ search: patientSearchInput.trim(), limit: 20, is_active: true })
        .then(r => { if (active) setPatientSearchResults(r.data?.items || []); })
        .catch(() => { if (active) setPatientSearchResults([]); })
        .finally(() => { if (active) setPatientSearchLoading(false); });
    }, 350);
    return () => { active = false; clearTimeout(timer); };
  }, [patientSearchInput, open]);

  useEffect(() => {
    if (open && billingEnabled) fetchPayments();
    if (!open) setPayments([]);
  }, [open, billingEnabled, fetchPayments]);

  useEffect(() => {
    if (!open) return;
    getAppointmentTypes().then(r => setAppointmentTypes(r.data || [])).catch(() => setAppointmentTypes([]));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (isEdit && selectedEvent) {
      setAppointmentData({
        patient_id: selectedEvent.patient_id || '',
        appointment_type_id: selectedEvent.appointment_type_id || '',
        duration_minutes: selectedEvent.duration_minutes,
        notes: selectedEvent.notes || '',
        state: selectedEvent.state || 'pendiente',
        is_overbooked: selectedEvent.is_overbooked || false,
        cancellation_reason: selectedEvent.cancellation_reason || '',
      });
      setStartDateTime(new Date(selectedEvent.start_time + 'Z'));
    } else if (selectedEvent?.start_time) {
      // patient_id inicial: antes siempre arrancaba vacío acá, sin
      // importar lo que llegara en selectedEvent — correcto cuando se crea
      // desde un click vacío en la grilla (CalendarGrid.js), pero rompía
      // el caso nuevo de "Nuevo turno" desde el perfil del paciente
      // (AppointmentsTab.js), que necesita que el paciente ya quede
      // precargado en vez de tener que volver a buscarlo a mano.
      setAppointmentData({
        patient_id: selectedEvent.patient_id || '',
        appointment_type_id: '',
        duration_minutes: doctor?.default_duration_minutes || 30,
        notes: '',
        state: 'pendiente',
        is_overbooked: false,
      });
      setStartDateTime(new Date(selectedEvent.start_time));
    }
    setShowQuickPatientForm(false);
    setQuickPatient({ first_name: '', last_name: '', phone: '+54 11 ', email: '', dni: '' });
    setQuickPatientDniWarning(null);
    setExpandedSection(null);
    setHistoryEntries([]);
    setHistoryLoaded(false);
    // Bug real reportado: el buscador de pacientes quedaba con el spinner
    // girando sin parar al reabrir el diálogo (incluso en un turno que ya
    // tenía paciente asignado, sin ninguna búsqueda en curso). Causa: este
    // reset general nunca tocaba patientSearchInput/Loading — si se
    // escribía algo y se cerraba el diálogo ANTES de que la request de
    // búsqueda (debounce 350ms + round-trip) terminara, el Dialog de MUI
    // sigue montado con open=false (no desmonta el componente), así que
    // ese estado viejo sobrevivía intacto a la próxima apertura. El cleanup
    // del efecto de búsqueda (ver más abajo) corta la request en vuelo,
    // pero solo se dispara si patientSearchInput vuelve a cambiar — nunca
    // pasaba solo por cerrar el diálogo.
    setPatientSearchInput('');
    setPatientSearchResults([]);
    setPatientSearchLoading(false);
  }, [open, isEdit, selectedEvent, doctor]);

  const buildPayload = (overrideOverbooked) => {
    const payload = {
      patient_id: appointmentData.patient_id || null,
      appointment_type_id: appointmentData.appointment_type_id || null,
      duration_minutes: appointmentData.duration_minutes,
      notes: appointmentData.notes || null,
      state: appointmentData.state,
      is_overbooked: overrideOverbooked ?? appointmentData.is_overbooked,
      start_time: startDateTime.toISOString(),
      cancellation_reason: appointmentData.state === 'cancelado' ? (appointmentData.cancellation_reason || null) : null,
    };
    if (!payload.patient_id && payload.state !== 'libre') payload.state = 'libre';
    if (payload.patient_id && payload.state === 'libre') payload.state = 'pendiente';
    return payload;
  };

  const submitAppointment = async (payload) => {
    let createdAppointment = null;
    if (isEdit) {
      await updateAppointment(selectedEvent.id, payload);
    } else {
      payload.doctor_id = doctorId;
      const res = await createAppointment(payload);
      createdAppointment = res.data;
    }
    // Si se cargó un pago inicial (seña) al crear el turno, se registra
    // recién ahora que ya existe un appointment_id real — el turno ya
    // quedó guardado antes de este paso, así que si el pago falla no se
    // pierde el turno, solo se avisa para que el usuario lo cargue a mano
    // desde el turno ya creado.
    if (createdAppointment && newTurnoPayment && (newTurnoPayment.amount || newTurnoPayment.status !== 'pendiente')) {
      try {
        await createPayment({
          patient_id: createdAppointment.patient_id,
          appointment_id: createdAppointment.id,
          amount: newTurnoPayment.amount || null,
          status: newTurnoPayment.status,
          payment_method: newTurnoPayment.payment_method || null,
          notes: newTurnoPayment.notes || null,
        });
      } catch {
        notify('Turno creado, pero no se pudo registrar el pago — cargalo desde el turno', 'warning');
      }
    }
    onClose();
    // Sugerir billing si se marcó como atendido y no hay pagos registrados
    if (isEdit && payload.state === 'atendido' && billingEnabled && payments.length === 0 && payload.patient_id) {
      notify('Turno atendido — ¿Registrás el pago? Abrí el turno para facturar.', 'info');
    } else {
      notify(isEdit ? 'Turno actualizado' : 'Turno creado', 'success');
    }
    setTimeout(onSaved, 300);
  };

  // El backend rechaza (409) turnos fuera del horario configurado del médico,
  // a menos que is_overbooked=true. En vez de solo mostrar el error, se ofrece
  // confirmar la creación como sobreturno.
  const isScheduleConflict = (detail) =>
    typeof detail === 'string' && (detail.includes('no atiende') || detail.includes('Fuera del horario'));

  const handleSubmit = async () => {
    if (!startDateTime) return;
    if (!appointmentData.duration_minutes || appointmentData.duration_minutes < 5) {
      notify('La duración mínima es 5 minutos', 'warning'); return;
    }
    const payload = buildPayload();
    try {
      await submitAppointment(payload);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (!payload.is_overbooked && isScheduleConflict(detail)) {
        setScheduleConflict(detail);
        return;
      }
      notify(err.userMessage || detail || 'Error al guardar el turno', 'error');
    }
  };

  // Único punto de entrada del botón "Crear turno" — si el mini-form de
  // paciente nuevo está abierto (y no estamos editando un turno existente),
  // delega en handleCreateQuickPatientAndSubmit para crear paciente+turno en
  // un solo paso. Antes había un botón de guardado duplicado dentro del
  // mini-form ("Crear paciente y turno") además de este, y confundía al
  // usuario tener dos botones de guardar visibles a la vez.
  const handlePrimarySubmit = () => {
    if (!isEdit && showQuickPatientForm) return handleCreateQuickPatientAndSubmit();
    return handleSubmit();
  };

  const confirmOverbookedSubmit = async () => {
    const detail = scheduleConflict;
    setScheduleConflict(null);
    try {
      await submitAppointment(buildPayload(true));
    } catch (err) {
      notify(err.userMessage || err.response?.data?.detail || detail || 'Error al guardar el turno', 'error');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('¿Eliminar este turno definitivamente?')) return;
    try {
      await deleteAppointment(selectedEvent.id);
      onClose();
      notify('Turno eliminado', 'info');
      onSaved();
    } catch (err) {
      notify(err.userMessage || 'Error al eliminar', 'error');
    }
  };

  // Antes eran dos clicks separados: "Crear y asignar" (creaba el paciente,
  // pero el turno seguía sin guardar) y después el botón grande de abajo
  // para recién ahí guardar el turno — confundía, se sentía como que ya
  // había terminado. Pedido explícito: un solo click que crea el paciente
  // Y guarda el turno. No se puede encadenar handleCreateQuickPatient()
  // + handleSubmit() porque setAppointmentData es asíncrono — se arma el
  // payload acá mismo con el id del paciente recién creado, sin esperar a
  // que el estado se actualice.
  const handleCreateQuickPatientAndSubmit = async () => {
    if (!quickPatient.first_name.trim() || !quickPatient.last_name.trim()) return;
    if (!startDateTime) { notify('Elegí fecha y hora del turno', 'warning'); return; }
    if (!appointmentData.duration_minutes || appointmentData.duration_minutes < 5) {
      notify('La duración mínima es 5 minutos', 'warning'); return;
    }
    let newPatient;
    try {
      const res = await createPatient(quickPatient, 'turno');
      newPatient = res.data;
      setPatients(prev => [newPatient, ...prev]);
    } catch (err) {
      notify(err.userMessage || 'Error al crear el paciente', 'error');
      return;
    }
    // El paciente ya se creó — si el turno falla de acá en más, no se
    // reintenta la creación del paciente (evita duplicados), se deja
    // asignado para que el usuario reintente guardar el turno (incluyendo
    // confirmar sobreturno) sin tener que volver a cargar los datos.
    setAppointmentData(prev => ({ ...prev, patient_id: newPatient.id }));
    const payload = { ...buildPayload(), patient_id: newPatient.id };
    try {
      await submitAppointment(payload);
      // Recepción suele querer mandar el link de autocompletado de datos
      // apenas termina de dar el turno — pedido explícito del usuario. Se
      // ofrece acá, no se envía solo. onClose() ya cerró este diálogo
      // (submitAppointment lo hace antes de notificar), así que el de
      // link queda como el único abierto.
      if (selfUpdateEnabled) {
        // El teléfono arranca precargado con "+54 11 " — si el
        // recepcionista no lo tocó, quickPatient.phone no es un número
        // real pero sigue siendo un string no vacío. Sin este chequeo,
        // SelfUpdateLinkDialog mostraría igual el botón de WhatsApp (ahí
        // solo valida "truthy") y abriría un chat roto a "5411". Se exige
        // que, descontado el prefijo de 4 dígitos (54+11), queden al menos
        // 6 dígitos reales de número.
        const phoneDigits = quickPatient.phone.replace(/\D/g, '');
        const hasRealPhone = phoneDigits.length >= 10;
        setSelfUpdateLinkFor({ patientId: newPatient.id, patientPhone: hasRealPhone ? quickPatient.phone : '' });
      }
      // El mini-form recién se cierra y resetea tras el éxito real del
      // turno — antes se cerraba ACÁ ARRIBA sin importar el resultado, así
      // que si el turno fallaba por conflicto de horario (abajo), el
      // formulario con los datos del paciente ya recién tipeados
      // desaparecía de la pantalla, dando la falsa sensación de que se
      // habían perdido (el paciente en realidad ya estaba creado en el
      // backend, pero visualmente no quedaba rastro de qué pasó). Bug real
      // reportado 2026-10-29.
      setShowQuickPatientForm(false);
      setQuickPatient({ first_name: '', last_name: '', phone: '+54 11 ', email: '', dni: '' });
      setQuickPatientDniWarning(null);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (!payload.is_overbooked && isScheduleConflict(detail)) {
        setScheduleConflict(detail);
        return;
      }
      notify(err.userMessage || detail || `Paciente "${newPatient.name}" creado, pero no se pudo guardar el turno — reintentá guardar`, 'error');
    }
  };

  // Usado solo al EDITAR un turno ya existente — ahí no conviene disparar
  // un guardado completo del turno solo por asignar el paciente (podría
  // pisar otros cambios en curso sin que el usuario lo pida). Al crear un
  // turno nuevo se usa handleCreateQuickPatientAndSubmit en su lugar.
  const handleCreateQuickPatient = async () => {
    if (!quickPatient.first_name.trim() || !quickPatient.last_name.trim()) return;
    try {
      const res = await createPatient(quickPatient, 'turno');
      const newPatient = res.data;
      setPatients(prev => [newPatient, ...prev]);
      setAppointmentData(prev => ({ ...prev, patient_id: newPatient.id }));
      setQuickPatient({ first_name: '', last_name: '', phone: '+54 11 ', email: '', dni: '' });
      setQuickPatientDniWarning(null);
      setShowQuickPatientForm(false);
      notify(`Paciente "${newPatient.name}" creado`, 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al crear paciente', 'error');
    }
  };

  const handleCreateQuickType = async () => {
    if (!quickType.name.trim()) return;
    setCreatingType(true);
    try {
      const res = await createAppointmentType(quickType);
      const newType = res.data;
      setAppointmentTypes(prev => [...prev, newType]);
      setAppointmentData(prev => ({
        ...prev,
        appointment_type_id: newType.id,
        duration_minutes: newType.default_duration_minutes,
      }));
      setQuickType({ name: '', default_duration_minutes: 30, color: '#1565c0' });
      setShowQuickTypeForm(false);
      notify(`Tipo de turno "${newType.name}" creado`, 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al crear el tipo de turno', 'error');
    } finally {
      setCreatingType(false);
    }
  };

  const logWhatsAppSent = async (patientId, messageType, preview) => {
    // El mensaje ya se abrió en WhatsApp (irreversible) antes de llamar acá
    // — si el registro en el historial de comunicaciones falla, avisamos en
    // vez de dejar el historial incompleto en silencio (mismo patrón que
    // ComunicacionesTab.js/RecetasTab.js).
    try {
      await logPatientMessage(patientId, { channel: 'whatsapp', message_type: messageType, message_preview: preview.slice(0, 200) });
    } catch {
      notify('WhatsApp abierto, pero no se pudo registrar en el historial de comunicaciones', 'warning');
    }
  };

  const sendWhatsAppTemplate = (template) => {
    const patient = patients.find(p => p.id === appointmentData.patient_id);
    if (!patient?.phone || !doctor || !startDateTime) return;
    const start = new Date(startDateTime);
    const mensaje = buildWhatsAppMessage(template.message_body, {
      patientName: patient.name,
      fecha: start.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' }),
      hora: start.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }),
      doctorName: doctor.name,
    });
    openWhatsApp(patient.phone, mensaje);
    // template.template_type es confirmacion|recordatorio|cancelacion|custom
    // (WhatsAppTemplate) — normalizado al vocabulario de PatientMessage.message_type
    // que ya usa email_utils.py para los mismos eventos (confirmacion_turno/recordatorio_turno).
    const msgType = template.template_type === 'confirmacion' ? 'confirmacion_turno'
      : template.template_type === 'cancelacion' ? 'cancelacion_turno'
      : 'recordatorio_turno';
    logWhatsAppSent(patient.id, msgType, mensaje);
  };

  const sendWhatsAppDefault = (type) => {
    const patient = patients.find(p => p.id === appointmentData.patient_id);
    if (!patient?.phone || !doctor || !startDateTime) return;
    const start = new Date(startDateTime);
    const fecha = start.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
    const hora = start.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    const mensaje = type === 'confirmacion'
      ? `Hola ${patient.name}, tu turno fue confirmado para el ${fecha} a las ${hora} con el Dr/a. ${doctor.name}.`
      : `Hola ${patient.name}, te recordamos tu turno el ${fecha} a las ${hora} con el Dr/a. ${doctor.name}.`;
    openWhatsApp(patient.phone, mensaje);
    logWhatsAppSent(patient.id, type === 'confirmacion' ? 'confirmacion_turno' : 'recordatorio_turno', mensaje);
  };

  const sendEmailManual = async (emailType) => {
    if (!selectedEvent?.id) return;
    setSendingEmail(true);
    try {
      await sendAppointmentEmail(selectedEvent.id, emailType);
      notify(emailType === 'confirmacion' ? 'Email de confirmación enviado' : 'Email de recordatorio enviado', 'success');
      // selectedEvent es una prop (el turno recargado vive en el padre) —
      // onSaved() dispara el mismo refresh que ya usa el resto del diálogo
      // tras cualquier cambio, así el chip "Recordatorio enviado" refleja
      // el reminder_sent_at real que el backend acaba de persistir.
      if (emailType === 'recordatorio') onSaved();
    } catch (err) {
      notify(err.userMessage || 'No se pudo enviar el email', 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleSavePayment = async () => {
    if (!paymentForm) return;
    setPaymentSaving(true);
    try {
      const payload = {
        patient_id: selectedEvent.patient_id,
        appointment_id: selectedEvent.id,
        amount: paymentForm.amount || null,
        status: paymentForm.status,
        payment_method: paymentForm.payment_method || null,
        notes: paymentForm.notes || null,
      };
      if (paymentForm.id) {
        await updatePayment(paymentForm.id, payload);
      } else {
        await createPayment(payload);
      }
      setPaymentForm(null);
      fetchPayments();
      notify(paymentForm.id ? 'Pago actualizado' : 'Pago registrado', 'success');
    } catch (err) {
      notify(err.userMessage || 'Error al guardar pago', 'error');
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!window.confirm('¿Eliminar este registro de pago?')) return;
    try {
      await deletePayment(payment.id);
      fetchPayments();
      notify('Pago eliminado', 'info');
    } catch (err) {
      notify(err.userMessage || 'Error al eliminar', 'error');
    }
  };

  // Una seña es dinero YA cobrado (adelanto) — suma al total cobrado del
  // turno junto con "pagado", no a "pendiente" (que implica "nada cobrado
  // todavía"). totalSeña se muestra aparte para saber cuánto de lo
  // cobrado en este turno todavía tiene un saldo abierto sin saldar.
  const totalPagado = payments
    .filter(p => p.status === 'pagado' || p.status === 'seña')
    .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const totalSeña = payments
    .filter(p => p.status === 'seña')
    .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const totalPendiente = payments
    .filter(p => p.status === 'pendiente')
    .reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);

  const toPatientOption = (p) => ({
    id: p.id,
    label: `${p.name}${p.dni ? ` — DNI ${p.dni}` : ''}${p.phone ? ` (${p.phone})` : ''}`,
    name: p.name,
    phone: p.phone,
  });
  const selectedPatientSource = selectedEvent?.patient?.id === appointmentData.patient_id
    ? selectedEvent.patient
    : patients.find(p => p.id === appointmentData.patient_id);
  const patientOptions = [
    ...(selectedPatientSource ? [toPatientOption(selectedPatientSource)] : []),
    ...patientSearchResults
      .filter(p => p.id !== selectedPatientSource?.id)
      .map(toPatientOption),
  ];
  const selectedPatientOption = patientOptions.find(o => o.id === appointmentData.patient_id) || null;
  const currentPatient = patients.find(p => p.id === appointmentData.patient_id);
  const hasPatientAndPhone = currentPatient?.phone || selectedPatientSource?.phone;
  const activeWaTemplates = (waTemplates || []).filter(t => t.is_active);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
            <Box>
              {isEdit ? 'Turno' : 'Nuevo Turno'}
            </Box>
            {isEdit && currentPatient && (
              <Button
                size="small"
                variant="outlined"
                endIcon={<OpenInNewIcon sx={{ fontSize: '0.85rem' }} />}
                onClick={() => { onClose(); navigate(`/patients/${currentPatient.id}`); }}
                sx={{ fontSize: '0.78rem', py: 0.4, textTransform: 'none', fontWeight: 600 }}
              >
                {currentPatient.name}
              </Button>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', gap: 1.5 }}>
              <DatePicker
                label="Fecha"
                value={startDateTime}
                onChange={setDatePart}
                disabled={!canEdit}
                sx={{ flex: 1.4 }}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TextField
                label="Hora"
                type="time"
                value={timeInputValue}
                onChange={e => setTimePart(e.target.value)}
                disabled={!canEdit}
                // Al crear un turno nuevo (no al editar uno existente), el
                // campo de hora arranca enfocado y con el segmento
                // seleccionado listo para escribir encima — pedido
                // explícito del usuario: usuarios con poca destreza con la
                // tecnología tardan mucho en ubicar el campo y hacer click
                // justo en el número a cambiar.
                autoFocus={!isEdit}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 300 }}
                sx={{ flex: 1 }}
              />
            </Box>

            {canEdit && doctorId && startDateTime && (
              <Box>
                <Button
                  size="small"
                  onClick={() => setShowSlots(s => !s)}
                  sx={{ textTransform: 'none', fontWeight: 600 }}
                >
                  {showSlots ? 'Ocultar horarios disponibles' : 'Ver horarios disponibles ese día'}
                </Button>
                {showSlots && (
                  <Box sx={{ mt: 1 }}>
                    {slotsLoading ? (
                      <CircularProgress size={18} />
                    ) : availableSlots.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        Sin huecos libres ese día (o el médico no tiene horario configurado).
                      </Typography>
                    ) : (
                      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        {availableSlots.map((iso) => {
                          const d = new Date(iso);
                          const label = d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
                          const isSelected = startDateTime && new Date(startDateTime).getTime() === d.getTime();
                          return (
                            <Chip
                              key={iso}
                              label={label}
                              size="small"
                              onClick={() => setStartDateTime(d)}
                              color={isSelected ? 'primary' : 'default'}
                              variant={isSelected ? 'filled' : 'outlined'}
                            />
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {isEdit && (
              <FormControl fullWidth size="small">
                <InputLabel>Estado del turno</InputLabel>
                <Select
                  value={appointmentData.state}
                  label="Estado del turno"
                  onChange={e => setAppointmentData(p => ({ ...p, state: e.target.value }))}
                  disabled={false}
                  renderValue={(val) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: STATE_CONFIG[val]?.color }} />
                      {STATE_CONFIG[val]?.label ?? val}
                    </Box>
                  )}
                >
                  {STATE_OPTIONS.map(opt => (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', flexShrink: 0, bgcolor: STATE_CONFIG[opt.value]?.color }} />
                        <Box>
                          <Typography variant="body2">{opt.label}</Typography>
                          <Typography variant="caption" color="text.secondary">{opt.hint}</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {appointmentData.state === 'cancelado' && (
              <TextField
                label="Motivo de cancelación"
                fullWidth
                size="small"
                multiline
                rows={2}
                value={appointmentData.cancellation_reason}
                onChange={e => setAppointmentData(p => ({ ...p, cancellation_reason: e.target.value }))}
                placeholder="Ej: Paciente reprogramó, no disponibilidad del médico…"
              />
            )}

            {isCancelingWithPatient && waitlistForDoctor.length > 0 && (
              <Box sx={{ p: 1.5, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.light', borderRadius: 1.5 }}>
                <Typography variant="caption" fontWeight={700} color="warning.dark" sx={{ display: 'block', mb: 0.5 }}>
                  Hay {waitlistForDoctor.length} paciente{waitlistForDoctor.length > 1 ? 's' : ''} en lista de espera para este médico
                </Typography>
                <Stack spacing={0.5}>
                  {waitlistForDoctor.map(w => (
                    <Box key={w.id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                      <Typography variant="body2">
                        {w.patient?.name}
                        {w.patient?.phone && <Typography component="span" variant="caption" color="text.secondary"> · {w.patient.phone}</Typography>}
                      </Typography>
                      {w.patient?.phone && (
                        <Button
                          size="small"
                          startIcon={<WhatsAppIcon sx={{ fontSize: 15 }} />}
                          onClick={() => openWhatsApp(w.patient.phone)}
                        >
                          Contactar
                        </Button>
                      )}
                    </Box>
                  ))}
                </Stack>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Se contacta manualmente — no se avisa solo.
                </Typography>
              </Box>
            )}

            {canEdit && (
              !showQuickPatientForm ? (
                <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 1.5 }}>
                  <Autocomplete
                    fullWidth
                    options={patientOptions}
                    getOptionLabel={(o) => o.label}
                    value={selectedPatientOption}
                    onChange={(_, val) => setAppointmentData(p => ({ ...p, patient_id: val?.id || '' }))}
                    onInputChange={(_, val, reason) => { if (reason === 'input') setPatientSearchInput(val); }}
                    filterOptions={(options) => options}
                    loading={patientSearchLoading}
                    renderInput={(params) => (
                      <TextField {...params} label="Paciente (buscar por nombre, DNI o tel.)" size="small"
                        placeholder="Mínimo 2 letras..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {patientSearchLoading && <CircularProgress size={16} sx={{ mr: 1 }} />}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    noOptionsText={patientSearchInput.trim().length < 2 ? 'Escribe al menos 2 letras' : 'Sin resultados'}
                    isOptionEqualToValue={(o, v) => o.id === v?.id}
                  />
                  <Button variant="outlined" size="small" sx={{ whiteSpace: 'nowrap', minWidth: 130 }}
                    onClick={() => setShowQuickPatientForm(true)}>
                    + Paciente nuevo
                  </Button>
                </Box>
              ) : (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed #ccc' }}>
                  <Typography variant="subtitle2" gutterBottom>Crear paciente nuevo</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField fullWidth size="small" label="Apellido *" value={quickPatient.last_name}
                      onChange={e => setQuickPatient(p => ({ ...p, last_name: e.target.value }))} />
                    <TextField fullWidth size="small" label="Nombre *" value={quickPatient.first_name}
                      onChange={e => setQuickPatient(p => ({ ...p, first_name: e.target.value }))} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField fullWidth size="small" label="Teléfono (WhatsApp)" value={quickPatient.phone}
                      onChange={e => setQuickPatient(p => ({ ...p, phone: e.target.value }))} />
                    <TextField fullWidth size="small" label="DNI" value={quickPatient.dni}
                      onChange={e => setQuickPatient(p => ({ ...p, dni: e.target.value }))}
                      onBlur={(e) => checkQuickPatientDni(e.target.value)}
                      InputProps={{ endAdornment: quickPatientDniChecking ? <InputAdornment position="end"><CircularProgress size={14} /></InputAdornment> : null }}
                    />
                  </Box>
                  {quickPatientDniWarning && (
                    <Alert severity="warning" sx={{ py: 0.5, mb: 1 }}>
                      Ya existe un paciente activo con este DNI:{' '}
                      <Link component={RouterLink} to={`/patients/${quickPatientDniWarning.id}`} fontWeight={700}>
                        {quickPatientDniWarning.name}
                      </Link>
                    </Alert>
                  )}
                  <TextField fullWidth size="small" label="Email" value={quickPatient.email}
                    onChange={e => setQuickPatient(p => ({ ...p, email: e.target.value }))} sx={{ mb: 1.5 }} />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setShowQuickPatientForm(false)}>Cancelar</Button>
                    {/* Al crear un turno nuevo, el único botón de guardado es
                        el "Crear turno" de abajo (ver handlePrimarySubmit) —
                        antes había un botón acá además del de abajo y
                        confundía (dos botones de guardar compitiendo). Al
                        EDITAR un turno ya existente sí tiene sentido un botón
                        propio acá, para no re-guardar otros cambios en curso
                        sin que el usuario lo pida explícitamente. */}
                    {isEdit && (
                      <Button variant="contained" size="small" disabled={!quickPatient.first_name.trim() || !quickPatient.last_name.trim()}
                        onClick={handleCreateQuickPatient}>
                        Crear y asignar
                      </Button>
                    )}
                  </Box>
                </Box>
              )
            )}

            {/* Antes este bloque entero desaparecía si la clínica no tenía
                ningún tipo de turno cargado — ahora siempre se muestra, con
                una opción "+ Crear tipo de turno nuevo" al final del Select
                que abre un mini-form inline (mismo patrón que "+ Paciente
                nuevo" arriba) para no tener que ir a Configuración. */}
            {canEdit && (
              !showQuickTypeForm ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo de turno</InputLabel>
                  <Select
                    label="Tipo de turno"
                    value={appointmentData.appointment_type_id}
                    onChange={e => {
                      const typeId = e.target.value;
                      if (typeId === '__new__') { setShowQuickTypeForm(true); return; }
                      const type = appointmentTypes.find(t => t.id === typeId);
                      setAppointmentData(p => ({
                        ...p,
                        appointment_type_id: typeId,
                        duration_minutes: type ? type.default_duration_minutes : p.duration_minutes,
                      }));
                    }}
                  >
                    <MenuItem value=""><em>Sin especificar</em></MenuItem>
                    {appointmentTypes.map(t => (
                      <MenuItem key={t.id} value={t.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: t.color, flexShrink: 0 }} />
                          {t.name} ({t.default_duration_minutes} min)
                        </Box>
                      </MenuItem>
                    ))}
                    <Divider />
                    <MenuItem value="__new__">
                      <Typography color="primary.main" fontWeight={600}>+ Crear tipo de turno nuevo</Typography>
                    </MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed #ccc' }}>
                  <Typography variant="subtitle2" gutterBottom>Crear tipo de turno nuevo</Typography>
                  <TextField fullWidth size="small" label="Nombre *" placeholder="Ej: Ecografía 5D"
                    value={quickType.name}
                    onChange={e => setQuickType(p => ({ ...p, name: e.target.value }))} sx={{ mb: 1 }} />
                  <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
                    <TextField size="small" label="Duración (min)" type="number"
                      value={quickType.default_duration_minutes}
                      onChange={e => setQuickType(p => ({ ...p, default_duration_minutes: Number(e.target.value) }))}
                      sx={{ flex: 1 }} />
                    <TextField size="small" label="Color" type="color"
                      value={quickType.color}
                      onChange={e => setQuickType(p => ({ ...p, color: e.target.value }))}
                      sx={{ flex: 1 }} />
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => setShowQuickTypeForm(false)} disabled={creatingType}>Cancelar</Button>
                    <Button variant="contained" size="small" disabled={!quickType.name.trim() || creatingType}
                      onClick={handleCreateQuickType}>
                      {creatingType ? <CircularProgress size={16} /> : 'Crear tipo'}
                    </Button>
                  </Box>
                </Box>
              )
            )}

            {canEdit && (
              <TextField fullWidth label="Duración (minutos)" type="number"
                value={appointmentData.duration_minutes}
                onChange={e => setAppointmentData(p => ({ ...p, duration_minutes: Number(e.target.value) }))}
                size="small"
              />
            )}

            {/* Pago inicial (seña) al crear el turno — antes el panel de
                Pagos exigía que el turno ya estuviera guardado (isEdit),
                así que había que guardar primero y volver a entrar. Se
                registra recién al confirmar "Crear turno" (ver
                submitAppointment), una vez que ya existe un appointment_id
                real al que asociarlo — para el usuario es un solo paso. */}
            {billingEnabledForNew && appointmentData.patient_id && (
              !newTurnoPayment ? (
                <Button size="small" startIcon={<AddIcon />} sx={{ alignSelf: 'flex-start' }}
                  onClick={() => setNewTurnoPayment({ ...EMPTY_PAYMENT })}>
                  Agregar pago (seña)
                </Button>
              ) : (
                <Box sx={{ p: 1.5, borderRadius: 1, border: '1px dashed', borderColor: 'primary.light', bgcolor: 'primary.50' }}>
                  <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                    Pago inicial
                  </Typography>
                  <Stack spacing={1}>
                    <Stack direction="row" spacing={1}>
                      <TextField
                        label="Monto ($)" size="small" type="number" sx={{ flex: 1 }}
                        value={newTurnoPayment.amount}
                        onChange={e => setNewTurnoPayment(f => ({ ...f, amount: e.target.value }))}
                        placeholder="Ej: 5000"
                      />
                      <FormControl size="small" sx={{ flex: 1 }}>
                        <InputLabel>Estado</InputLabel>
                        <Select value={newTurnoPayment.status} label="Estado"
                          onChange={e => setNewTurnoPayment(f => ({ ...f, status: e.target.value }))}>
                          {Object.entries(PAYMENT_STATUS_CONFIG).map(([v, c]) => (
                            <MenuItem key={v} value={v}>{c.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Stack>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Método de pago</InputLabel>
                      <Select value={newTurnoPayment.payment_method} label="Método de pago"
                        onChange={e => setNewTurnoPayment(f => ({ ...f, payment_method: e.target.value }))}>
                        <MenuItem value="">— ninguno —</MenuItem>
                        {PAYMENT_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Nota" size="small" fullWidth
                      value={newTurnoPayment.notes}
                      onChange={e => setNewTurnoPayment(f => ({ ...f, notes: e.target.value }))}
                      placeholder="Ej: reserva, pago parcial..."
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button size="small" onClick={() => setNewTurnoPayment(null)}>Quitar pago</Button>
                    </Box>
                  </Stack>
                </Box>
              )
            )}

            <TextField fullWidth label="Notas" multiline rows={3}
              value={appointmentData.notes}
              onChange={e => setAppointmentData(p => ({ ...p, notes: e.target.value }))}
            />

            {canEdit && (
              <FormControlLabel
                control={
                  <Checkbox checked={appointmentData.is_overbooked}
                    onChange={e => setAppointmentData(p => ({ ...p, is_overbooked: e.target.checked }))} />
                }
                label="Sobreturno permitido"
              />
            )}

            {/* ── Secciones colapsables: Pagos / Comunicación / Historial ── */}
            {(billingEnabled && selectedEvent?.patient_id
              || (features?.whatsapp && isEdit && hasPatientAndPhone && doctor)
              || (isEdit && selectedEvent?.patient_id && patients.find(p => p.id === selectedEvent.patient_id)?.email)
              || isEdit) && (
              <>
                <Divider />
                <Stack spacing={1}>

                  {billingEnabled && selectedEvent?.patient_id && (
                    <SectionAccordion
                      ref={(el) => { sectionRefs.current.billing = el; }}
                      expanded={expandedSection === 'billing'}
                      onChange={toggleSection('billing')}
                      icon={<AttachMoneyIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                      title="Pagos"
                      badge={
                        <Stack direction="row" spacing={0.5} sx={{ ml: 'auto', mr: 1 }}>
                          {paymentsLoading && <CircularProgress size={12} />}
                          {totalPagado > 0 && (
                            <Chip size="small" color="success" label={`Cobrado $${totalPagado.toLocaleString('es-AR')}`} sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                          {totalSeña > 0 && (
                            <Chip size="small" color="secondary" label={`Seña $${totalSeña.toLocaleString('es-AR')}`} sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                          {totalPendiente > 0 && (
                            <Chip size="small" color="warning" label={`Pendiente $${totalPendiente.toLocaleString('es-AR')}`} sx={{ height: 20, fontSize: '0.7rem' }} />
                          )}
                        </Stack>
                      }
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 1 }}>
                        {totalSeña > 0 && (
                          <Button size="small" color="secondary" variant="outlined"
                            onClick={() => setPaymentForm({ ...EMPTY_PAYMENT, status: 'pagado', notes: 'Saldo de seña' })}>
                            Registrar saldo
                          </Button>
                        )}
                        <Button size="small" startIcon={<AddIcon />} onClick={() => setPaymentForm({ ...EMPTY_PAYMENT })}>
                          Agregar pago
                        </Button>
                      </Box>

                      {payments.length === 0 && !paymentsLoading && (
                        <Typography variant="caption" color="text.secondary">Sin pagos registrados para este turno</Typography>
                      )}
                      <Stack spacing={0.5}>
                        {payments.map(p => {
                          const cfg = PAYMENT_STATUS_CONFIG[p.status] || { label: p.status, color: 'default' };
                          return (
                            <Box key={p.id} sx={{
                              display: 'flex', alignItems: 'center', gap: 1,
                              p: 1, borderRadius: 1, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider',
                            }}>
                              <Chip label={cfg.label} color={cfg.color} size="small" sx={{ minWidth: 80 }} />
                              <Typography variant="body2" sx={{ flex: 1 }}>
                                {p.amount ? `$${parseFloat(p.amount).toLocaleString('es-AR')}` : 'Sin monto'}
                                {p.payment_method && <Typography component="span" variant="caption" color="text.secondary"> · {p.payment_method}</Typography>}
                                {p.notes && <Typography component="span" variant="caption" color="text.secondary"> · {p.notes}</Typography>}
                              </Typography>
                              <Tooltip title="Editar">
                                <IconButton size="small" onClick={() => setPaymentForm({ ...p, amount: p.amount || '' })}>
                                  <EditIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Eliminar">
                                <IconButton size="small" color="error" onClick={() => handleDeletePayment(p)}>
                                  <DeleteIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          );
                        })}
                      </Stack>

                      {paymentForm && (
                        <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, border: '1px dashed', borderColor: 'primary.light', bgcolor: 'primary.50' }}>
                          <Typography variant="caption" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                            {paymentForm.id ? 'Editar pago' : 'Nuevo pago'}
                          </Typography>
                          <Stack spacing={1}>
                            <Stack direction="row" spacing={1}>
                              <TextField
                                label="Monto ($)" size="small" type="number" sx={{ flex: 1 }}
                                value={paymentForm.amount}
                                onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))}
                                placeholder="Ej: 5000"
                              />
                              <FormControl size="small" sx={{ flex: 1 }}>
                                <InputLabel>Estado</InputLabel>
                                <Select value={paymentForm.status} label="Estado"
                                  onChange={e => setPaymentForm(f => ({ ...f, status: e.target.value }))}>
                                  {Object.entries(PAYMENT_STATUS_CONFIG).map(([v, c]) => (
                                    <MenuItem key={v} value={v}>{c.label}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </Stack>
                            <FormControl size="small" fullWidth>
                              <InputLabel>Método de pago</InputLabel>
                              <Select value={paymentForm.payment_method} label="Método de pago"
                                onChange={e => setPaymentForm(f => ({ ...f, payment_method: e.target.value }))}>
                                <MenuItem value="">— ninguno —</MenuItem>
                                {PAYMENT_METHODS.map(m => <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>)}
                              </Select>
                            </FormControl>
                            <TextField
                              label="Nota" size="small" fullWidth
                              value={paymentForm.notes}
                              onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))}
                              placeholder="Ej: reserva, pago parcial, pendiente diferencia..."
                            />
                            <Stack direction="row" spacing={1} justifyContent="flex-end">
                              <Button size="small" onClick={() => setPaymentForm(null)}>Cancelar</Button>
                              <Button size="small" variant="contained" onClick={handleSavePayment} disabled={paymentSaving}>
                                {paymentSaving ? <CircularProgress size={14} /> : 'Guardar'}
                              </Button>
                            </Stack>
                          </Stack>
                        </Box>
                      )}
                    </SectionAccordion>
                  )}

                  {isEdit && doctor && (() => {
                    // selectedEvent.patient (PatientSummary, viene siempre
                    // con la respuesta del turno) es la fuente confiable —
                    // el array `patients` (prop) puede estar vacío según
                    // desde qué pantalla se abrió el diálogo (ver
                    // selectedPatientSource, mismo patrón, un poco más
                    // arriba en este componente).
                    const patient = selectedEvent?.patient?.id === selectedEvent?.patient_id
                      ? selectedEvent.patient
                      : (patients.find(p => p.id === selectedEvent?.patient_id) || currentPatient);
                    const canWhatsApp = features?.whatsapp && !!patient?.phone;
                    const canEmail = !!patient?.email;
                    if (!canWhatsApp && !canEmail) return null;
                    // Si el canal recordado ya no está disponible para este
                    // paciente (ej. cambió a uno sin teléfono), arranca en
                    // el que sí lo esté en vez de mostrar una sección vacía.
                    const channel = (commChannel === 'whatsapp' && !canWhatsApp) ? 'email'
                      : (commChannel === 'email' && !canEmail) ? 'whatsapp'
                      : commChannel;
                    const reminderSent = selectedEvent?.reminder_sent_at;

                    return (
                      <SectionAccordion
                        ref={(el) => { sectionRefs.current.comunicacion = el; }}
                        expanded={expandedSection === 'comunicacion'}
                        onChange={toggleSection('comunicacion')}
                        icon={<WhatsAppIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                        title="Comunicación"
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {/* Elegir canal — antes WhatsApp y Email vivían en
                              secciones separadas; pedido explícito del
                              usuario: un solo lugar para elegir por cuál
                              enviar, ya que ambos canales ya existían. */}
                          <ToggleButtonGroup
                            value={channel}
                            exclusive
                            size="small"
                            onChange={(_, v) => v && setCommChannel(v)}
                          >
                            <ToggleButton value="whatsapp" disabled={!canWhatsApp}>
                              <WhatsAppIcon sx={{ fontSize: 16, mr: 0.5, color: canWhatsApp ? '#25D366' : undefined }} />
                              WhatsApp
                            </ToggleButton>
                            <ToggleButton value="email" disabled={!canEmail}>
                              <EmailIcon sx={{ fontSize: 16, mr: 0.5 }} />
                              Email
                            </ToggleButton>
                          </ToggleButtonGroup>

                          {channel === 'whatsapp' && canWhatsApp && (
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                              {activeWaTemplates.length > 0
                                ? activeWaTemplates.map(tmpl => (
                                    <Button key={tmpl.id} variant="outlined" size="small"
                                      startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
                                      onClick={() => sendWhatsAppTemplate(tmpl)}>
                                      {tmpl.name}
                                    </Button>
                                  ))
                                : (
                                  <>
                                    <Button variant="outlined" size="small"
                                      startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
                                      onClick={() => sendWhatsAppDefault('confirmacion')}>
                                      Confirmación
                                    </Button>
                                    <Button variant="outlined" size="small"
                                      startIcon={<WhatsAppIcon sx={{ color: '#25D366' }} />}
                                      onClick={() => sendWhatsAppDefault('recordatorio')}>
                                      Recordatorio
                                    </Button>
                                  </>
                                )
                              }
                            </Box>
                          )}

                          {channel === 'email' && canEmail && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <Button variant="outlined" size="small" disabled={sendingEmail}
                                  startIcon={sendingEmail ? <CircularProgress size={14} /> : <EmailIcon />}
                                  onClick={() => sendEmailManual('confirmacion')}>
                                  Confirmación
                                </Button>
                                <Button variant="outlined" size="small" disabled={sendingEmail}
                                  startIcon={sendingEmail ? <CircularProgress size={14} /> : <EmailIcon />}
                                  onClick={() => sendEmailManual('recordatorio')}>
                                  Recordatorio
                                </Button>
                              </Box>
                              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                                {reminderSent ? (
                                  <Chip
                                    size="small"
                                    icon={<CheckCircleIcon sx={{ fontSize: '14px !important' }} />}
                                    label={`Recordatorio enviado ${new Date(reminderSent + 'Z').toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit' })}`}
                                    color="success"
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                  />
                                ) : (
                                  <Chip
                                    size="small"
                                    label="Recordatorio pendiente"
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.7rem', color: 'text.disabled', borderColor: 'divider' }}
                                  />
                                )}
                              </Stack>
                            </Box>
                          )}
                        </Box>
                      </SectionAccordion>
                    );
                  })()}

                  {isEdit && (
                    <SectionAccordion
                      ref={(el) => { sectionRefs.current.history = el; }}
                      expanded={showHistory}
                      onChange={toggleSection('history')}
                      icon={<HistoryIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
                      title="Historial de cambios"
                    >
                      {historyLoading ? (
                        <CircularProgress size={18} />
                      ) : historyEntries.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">Sin historial registrado.</Typography>
                      ) : (
                        <Stack spacing={0.75}>
                          {historyEntries.map(entry => (
                            <AppointmentHistoryEntry key={entry.id} entry={entry} />
                          ))}
                        </Stack>
                      )}
                    </SectionAccordion>
                  )}

                </Stack>
              </>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ p: isMobile ? 2 : 3, flexWrap: 'wrap', gap: 1 }}>
          <Button onClick={onClose} fullWidth={isMobile}>Cerrar</Button>
          {isEdit && canEdit && (
            <Button color="error" onClick={handleDelete} fullWidth={isMobile}>Eliminar turno</Button>
          )}
          {features?.waiting_room && isEdit && canEdit && selectedEvent?.patient_id && !selectedEvent?.checked_in_at &&
            !['atendido', 'cancelado', 'ausente'].includes(selectedEvent?.state) && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={checkingIn ? null : <HowToRegIcon />}
              disabled={checkingIn}
              fullWidth={isMobile}
              onClick={async () => {
                setCheckingIn(true);
                try {
                  await checkInAppointment(selectedEvent.id, {});
                  notify(`${currentPatient?.name || 'Paciente'} recepcionado`, 'success');
                  onClose();
                  setTimeout(onSaved, 300);
                } catch (err) {
                  notify(err.userMessage || 'Error al recepcionar', 'error');
                } finally {
                  setCheckingIn(false);
                }
              }}
            >
              {checkingIn ? 'Recepcionando...' : 'Recepcionar'}
            </Button>
          )}
          {canEdit && (
            <Button
              variant="contained"
              onClick={handlePrimarySubmit}
              disabled={!startDateTime || (!isEdit && showQuickPatientForm && (!quickPatient.first_name.trim() || !quickPatient.last_name.trim()))}
              fullWidth={isMobile}
            >
              {isEdit ? 'Guardar' : 'Crear turno'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={!!scheduleConflict} onClose={() => setScheduleConflict(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Fuera del horario del médico</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {scheduleConflict}. ¿Querés crear el turno igualmente como sobreturno?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleConflict(null)}>Cancelar</Button>
          <Button variant="contained" color="warning" onClick={confirmOverbookedSubmit}>
            Crear como sobreturno
          </Button>
        </DialogActions>
      </Dialog>

      <SelfUpdateLinkDialog
        open={!!selfUpdateLinkFor}
        onClose={() => setSelfUpdateLinkFor(null)}
        patientId={selfUpdateLinkFor?.patientId}
        patientPhone={selfUpdateLinkFor?.patientPhone}
      />
    </LocalizationProvider>
  );
};

export default AppointmentDialog;
